/**
 * Sends a batch immediately on each [[BatchingLayer]] "BatchPending" event.
 *
 * This is
 * the fastest-sending reasonable [[BatchingStrategy]].
 */
export class ImmediateBatchingStrategy {
    constructor() {
        this.batchingLayer = undefined;
        this.unsubscribe = undefined;
    }
    start(batchingLayer) {
        this.batchingLayer = batchingLayer;
        this.unsubscribe = this.batchingLayer.on("BatchPending", () => this.batchingLayer.commitBatch());
    }
    stop() {
        this.unsubscribe();
        this.batchingLayer = undefined;
        this.unsubscribe = undefined;
    }
}
/**
 * Sends at most one batch per time period, otherwise sending
 * as soon as possible.
 */
export class RateLimitBatchingStrategy {
    /**
     * @param periodMs The length of the time period in ms
     * @param commitOnReceive If true, whenever `batchingLayer`
     * would receive a message from another replica
     * except that there is a pending batch ("ReceiveBlocked" event),
     * this commits the batch immediately, so that the received message can be processed
     * immediately afterwards.  This may cause the rate limit to be
     * exceeded.
     */
    constructor(periodMs) {
        this.periodMs = periodMs;
        this.batchingLayer = undefined;
        this.unsubscribeBatchPending = undefined;
        this.unsubscribeReceiveBlocked = undefined;
        this.pendingBatch = false;
        this.lastSend = -1;
        this.commitBatch = () => {
            if (this.batchingLayer !== undefined) {
                this.pendingBatch = false;
                this.lastSend = Date.now();
                this.batchingLayer.commitBatch();
            }
        };
    }
    start(batchingLayer) {
        this.batchingLayer = batchingLayer;
        this.unsubscribeBatchPending = this.batchingLayer.on("BatchPending", this.onBatchPending.bind(this));
    }
    stop() {
        this.unsubscribeBatchPending();
        if (this.unsubscribeReceiveBlocked)
            this.unsubscribeReceiveBlocked();
        this.batchingLayer = undefined;
        this.unsubscribeBatchPending = undefined;
        this.unsubscribeReceiveBlocked = undefined;
    }
    onBatchPending() {
        if (!this.pendingBatch) {
            const time = Date.now();
            // If it has been more then periodMs ms since the last
            // batch, commit immediately.
            if (this.lastSend === -1 || this.lastSend + this.periodMs <= time) {
                this.commitBatch();
            }
            else {
                // Wait until periodMs ms have elapsed since lastSend.
                this.pendingBatch = true;
                setTimeout(this.commitBatch, Math.min(this.lastSend + this.periodMs - time, this.periodMs));
            }
        }
    }
}
/**
 * Does nothing; you must call [[BatchingLayer.commitBatch]] manually
 * when you want to send a batch.
 */
export class ManualBatchingStrategy {
    start(_batchingLayer) {
        // Does nothing.
    }
    stop() {
        // Does nothing.
    }
}
//# sourceMappingURL=batching_strategy.js.map